import { AlertCallout } from "@spz/ui/components/alert-callout";
import { IconButton } from "@spz/ui/components/button";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type {
  AlertCalloutOptions,
  AlertCalloutType,
} from "./alert-callout-types";

// Toast duration mapping (matches original implementation)
const TOAST_DURATIONS: Record<AlertCalloutType, number> = {
  success: 3000,
  critical: 5000,
  warning: 5000,
  loading: Number.POSITIVE_INFINITY,
  info: 3000,
};

// ARIA labels for screen readers
const ARIA_LABELS: Record<AlertCalloutType, string> = {
  critical: "Error",
  warning: "Warning",
  info: "Information",
  success: "Success",
  loading: "Loading",
};

/**
 * Shows an alert callout notification using Sonner toast system
 *
 * @param options - Alert configuration
 * @returns Toast ID for dismissal
 */
export const showAlertCallout = ({
  type,
  title,
  description,
}: AlertCalloutOptions): string | number => {
  // Wrapper component to access i18n hook
  const ToastContent = () => {
    const { t } = useTranslation();

    return (
      <AlertCallout
        variant={type}
        title={title}
        description={description}
        ariaLabel={ARIA_LABELS[type]}
        closeButtonSlot={
          <IconButton
            icon="x"
            variant="simple"
            className="text-gray-900 cursor-pointer"
            onClick={() => toast.dismiss(toastId)}
            aria-label={t("close_notification")}
          />
        }
      />
    );
  };

  // Use Sonner's custom toast for headless rendering
  const toastId = toast.custom(() => <ToastContent />, {
    duration: TOAST_DURATIONS[type],
  });

  return toastId;
};
